/**
 * A list row that can be swiped right for its positive action (§6 gestures). Always paired with a visible button in
 * the row, so the gesture is a shortcut, never the only way. Vertical scrolling is left alone.
 */
import { useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { Check } from 'lucide-react';
import { swipeCommits } from '@tradeoutlet/shared';

export function SwipeRow({ children, onSwipe, label, disabled, testId }: { children: ReactNode; onSwipe: () => void; label: string; disabled?: boolean; testId?: string }) {
  const start = useRef<{ x: number; y: number; w: number } | null>(null);
  const [dx, setDx] = useState(0);
  const [horizontal, setHorizontal] = useState(false);
  const end = (commit: boolean) => { if (commit) onSwipe(); start.current = null; setDx(0); setHorizontal(false); };
  return (
    // pan-y: the browser keeps vertical scrolling but leaves sideways drags to us (no back-swipe navigation mid-row).
    <div className="relative touch-pan-y overflow-hidden" data-testid={testId}
      onTouchStart={disabled ? undefined : e => { const t = e.touches[0]; start.current = { x: t.clientX, y: t.clientY, w: e.currentTarget.clientWidth }; }}
      onTouchMove={disabled ? undefined : e => {
        const s = start.current; if (!s) return;
        const t = e.touches[0]; const x = t.clientX - s.x; const y = t.clientY - s.y;
        if (!horizontal && Math.abs(y) > Math.abs(x)) { start.current = null; return; }
        if (Math.abs(x) > 8) setHorizontal(true);
        setDx(Math.max(0, x));
      }}
      onTouchEnd={disabled ? undefined : e => { const s = start.current; if (!s) return end(false); const t = e.changedTouches[0]; end(swipeCommits(t.clientX - s.x, t.clientY - s.y, s.w)); }}
      onTouchCancel={() => end(false)}>
      {!disabled && (
        <div className={clsx('absolute inset-0 flex items-center gap-2 bg-emerald-600 pl-5 text-sm font-semibold text-white', dx === 0 && 'invisible')} aria-hidden>
          <Check className="h-5 w-5" />{label}
        </div>
      )}
      <div className={clsx('relative bg-white dark:bg-slate-900', dx === 0 && 'transition-transform duration-150')} style={dx ? { transform: `translateX(${dx}px)` } : undefined}>{children}</div>
    </div>
  );
}
